import { Tooltip, TooltipProps, Typography, TypographyProps, useTheme } from '@mui/material'
import { PrometheusAlert, RuuviTagBattery, RuuviTagCondition, Sensor, ValveSensor } from '../types'
import { Trans, TransProps } from 'react-i18next'
import { AlertRuleName } from '../enums'
import {
  formatAlertingCurrentValueBySensor,
  formatAlertParam1Value,
  formatAlertParam2Value,
  formatAlertValue,
  formatAlertValueBySensor,
  formatAlertValueByValveSensor,
  getAlertParam1Value,
  getAlertValueUnit,
  translateAlertPhase
} from '../utils/alert'
import {
  formatBatteryVoltage,
  formatCurrent,
  formatHumidity,
  formatPercentage,
  formatPressure,
  formatRssi,
  formatTemperature
} from '../utils/string'
import { formatValveSensorPosition } from '../utils/valve'
import { ReactElement } from 'react'

export type AlertTranslationKey = 'summary' | 'eval' | 'expression'

export interface AlertSummaryProps extends Omit<TypographyProps, 'children'> {
  alert: PrometheusAlert
  sensor?: Sensor | null
  valveSensor?: ValveSensor | null
  ruuviTagCondition?: RuuviTagCondition | null
  ruuviTagBattery?: RuuviTagBattery | null
  translationKey?: AlertTranslationKey
}

export default function AlertSummary({
  alert,
  sensor,
  valveSensor,
  ruuviTagCondition,
  ruuviTagBattery,
  translationKey = 'summary',
  sx,
  ...props
}: AlertSummaryProps) {
  return (
    <Typography sx={{ ...sx }} {...props}>
      <AlertTranslation
        alert={alert}
        sensor={sensor}
        valveSensor={valveSensor}
        ruuviTagCondition={ruuviTagCondition}
        ruuviTagBattery={ruuviTagBattery}
        translationKey={translationKey}
      />
    </Typography>
  )
}

interface AlertTranslationProps {
  alert: PrometheusAlert
  sensor?: Sensor | null
  valveSensor?: ValveSensor | null
  ruuviTagCondition?: RuuviTagCondition | null
  ruuviTagBattery?: RuuviTagBattery | null
  translationKey?: AlertTranslationKey
}

function AlertTranslation({
  alert,
  sensor,
  valveSensor,
  ruuviTagCondition,
  ruuviTagBattery,
  translationKey = 'summary'
}: AlertTranslationProps) {
  switch (alert.labels.alertname) {
    // Current
    case AlertRuleName.SHORT_CIRCUIT:
    case AlertRuleName.OVERLOADED_CURRENT:
    case AlertRuleName.HIGH_CURRENT:
    case AlertRuleName.LOW_CURRENT:
      return (
        <CurrentAlertTranslation alert={alert} sensor={sensor} translationKey={translationKey} />
      )

    // Device
    case AlertRuleName.DEVICE_OFFLINE:
      return <DeviceOfflineAlertTranslation alert={alert} translationKey={translationKey} />

    // Signal
    case AlertRuleName.LOW_SIGNAL:
      return (
        <SignalAlertTranslation alert={alert} sensor={sensor} translationKey={translationKey} />
      )

    // Voltage
    case AlertRuleName.LOW_VOLTAGE:
    case AlertRuleName.NO_VOLTAGE:
    case AlertRuleName.HIGH_VOLTAGE:
      return (
        <VoltageAlertTranslation alert={alert} sensor={sensor} translationKey={translationKey} />
      )

    // Battery
    case AlertRuleName.LOW_BATTERY:
      return (
        <BatteryAlertTranslation
          alert={alert}
          ruuviTagBattery={ruuviTagBattery}
          translationKey={translationKey}
        />
      )

    // Dew Point
    case AlertRuleName.DEW_POINT:
      return (
        <DewPointAlertTranslation
          alert={alert}
          ruuviTagCondition={ruuviTagCondition}
          translationKey={translationKey}
        />
      )

    // Humidity
    case AlertRuleName.HIGH_HUMIDITY:
    case AlertRuleName.LOW_HUMIDITY:
      return (
        <HumidityAlertTranslation
          alert={alert}
          sensor={sensor}
          ruuviTagCondition={ruuviTagCondition}
          translationKey={translationKey}
        />
      )

    // Pressure
    case AlertRuleName.HIGH_PRESSURE:
    case AlertRuleName.LOW_PRESSURE:
      return (
        <PressureAlertTranslation
          alert={alert}
          ruuviTagCondition={ruuviTagCondition}
          translationKey={translationKey}
        />
      )

    // Temperature
    case AlertRuleName.HIGH_TEMPERATURE:
    case AlertRuleName.LOW_TEMPERATURE:
      return (
        <TemperatureAlertTranslation
          alert={alert}
          sensor={sensor}
          ruuviTagCondition={ruuviTagCondition}
          translationKey={translationKey}
        />
      )

    // CondensationRiskIntake / CondensationRiskExhaust
    case AlertRuleName.CONDENSATION_RISK_INTAKE:
    case AlertRuleName.CONDENSATION_RISK_EXHAUST:
      return (
        <CondensationRiskAlertTranslation
          alert={alert}
          valveSensor={valveSensor}
          translationKey={translationKey}
        />
      )

    // ExhaustAirTooCold / ExhaustAirTooHot
    case AlertRuleName.EXHAUST_AIR_TOO_COLD:
    case AlertRuleName.EXHAUST_AIR_TOO_HOT:
      return (
        <ExhaustAirAlertTranslation
          alert={alert}
          valveSensor={valveSensor}
          translationKey={translationKey}
        />
      )

    // HeatingWhileValveClosed
    case AlertRuleName.HEATING_WHILE_VALVE_CLOSED:
      return (
        <HeatingWhileValveClosedAlertTranslation
          alert={alert}
          valveSensor={valveSensor}
          translationKey={translationKey}
        />
      )

    // NoHeating
    case AlertRuleName.NO_HEATING:
      return (
        <NoHeatingAlertTranslation
          alert={alert}
          valveSensor={valveSensor}
          translationKey={translationKey}
        />
      )

    // TargetTemperatureNotReached
    case AlertRuleName.TARGET_TEMPERATURE_NOT_REACHED:
      return (
        <TargetTemperatureNotReachedAlertTranslation
          alert={alert}
          valveSensor={valveSensor}
          translationKey={translationKey}
        />
      )

    // TemperatureRapidChange
    case AlertRuleName.RAPID_TEMPERATURE_DROP:
    case AlertRuleName.RAPID_TEMPERATURE_RISE:
      return (
        <TemperatureRapidChangeAlertTranslation
          alert={alert}
          valveSensor={valveSensor}
          translationKey={translationKey}
        />
      )

    // WeakSignal
    case AlertRuleName.WEAK_SIGNAL:
      return (
        <WeakSignalAlertTranslation
          alert={alert}
          valveSensor={valveSensor}
          translationKey={translationKey}
        />
      )

    default:
      return <AlertTrans alert={alert} translationKey={translationKey} />
  }
}

type DeviceAlertTranslationProps = Pick<
  AlertTranslationProps,
  'alert' | 'sensor' | 'translationKey'
>

type RuuviTagAlertTranslationProps = Pick<
  AlertTranslationProps,
  'alert' | 'ruuviTagCondition' | 'ruuviTagBattery' | 'translationKey'
>

type ValveAlertTranslationProps = Pick<
  AlertTranslationProps,
  'alert' | 'valveSensor' | 'translationKey'
>

/**
 * --------------------------------------------------------------------------------------------------------------------
 * Device alerts
 * --------------------------------------------------------------------------------------------------------------------
 */

function CurrentAlertTranslation({ alert, sensor, translationKey }: DeviceAlertTranslationProps) {
  return (
    <AlertTrans
      alert={alert}
      translationKey={translationKey}
      components={{
        value: sensor
          ? formatAlertValueBySensor(alert, sensor, false)
          : formatAlertValue(alert, false),
        ValueTooltip: (
          <ValueTooltip
            value={sensor ? formatAlertValueBySensor(alert, sensor) : formatAlertValue(alert)}
          />
        ),
        'sensor.c': sensor && formatAlertingCurrentValueBySensor(alert, sensor, false),
        SensorCurrentTooltip: (
          <ValueTooltip
            value={sensor ? formatAlertingCurrentValueBySensor(alert, sensor) : undefined}
          />
        ),
        'sensor.rc': sensor && formatCurrent(sensor.rated_current, false, 0),
        SensorRatedCurrentTooltip: (
          <ValueTooltip value={sensor ? formatCurrent(sensor.rated_current, true, 0) : undefined} />
        )
      }}
    />
  )
}

function DeviceOfflineAlertTranslation({
  alert,
  translationKey
}: Pick<AlertTranslationProps, 'alert' | 'translationKey'>) {
  return (
    <AlertTrans
      alert={alert}
      translationKey={translationKey}
      components={{
        // This is undefined for device alerts so we provide a default value of "5 minutes".
        param1: getAlertParam1Value(alert)?.toString() ?? '5'
      }}
    />
  )
}

function DeviceHumidityAlertTranslation({
  alert,
  sensor,
  translationKey
}: DeviceAlertTranslationProps) {
  return (
    <AlertTrans
      alert={alert}
      i18nKey={`AlertRule.DeviceHighHumidity.${translationKey}`}
      translationKey={translationKey}
      components={{
        value: sensor ? formatHumidity(sensor.rh, false) : formatAlertValue(alert, false),
        ValueTooltip: (
          <ValueTooltip value={sensor ? formatHumidity(sensor.rh) : formatAlertValue(alert)} />
        ),
        'sensor.rh': sensor ? formatHumidity(sensor.rh, false) : formatAlertValue(alert, false),
        SensorHumidityTooltip: (
          <ValueTooltip value={sensor ? formatHumidity(sensor.rh) : formatAlertValue(alert)} />
        )
      }}
    />
  )
}

function SignalAlertTranslation({ alert, sensor, translationKey }: DeviceAlertTranslationProps) {
  return (
    <AlertTrans
      alert={alert}
      translationKey={translationKey}
      components={{
        value: sensor
          ? formatAlertValueBySensor(alert, sensor, false)
          : formatAlertValue(alert, false),
        ValueTooltip: (
          <ValueTooltip
            value={sensor ? formatAlertValueBySensor(alert, sensor) : formatAlertValue(alert)}
          />
        ),
        'sensor.rssi': sensor ? formatRssi(sensor.rssi, false) : formatAlertValue(alert, false),
        SensorRssiTooltip: (
          <ValueTooltip value={sensor ? formatRssi(sensor.rssi) : formatAlertValue(alert)} />
        )
      }}
    />
  )
}

function DeviceTemperatureAlertTranslation({
  alert,
  sensor,
  translationKey
}: DeviceAlertTranslationProps) {
  return (
    <AlertTrans
      alert={alert}
      i18nKey={`AlertRule.DeviceHighTemperature.${translationKey}`}
      translationKey={translationKey}
      components={{
        value: sensor ? formatTemperature(sensor.temp, false) : formatAlertValue(alert, false),
        ValueTooltip: (
          <ValueTooltip value={sensor ? formatTemperature(sensor.temp) : formatAlertValue(alert)} />
        ),
        'sensor.temp': sensor
          ? formatTemperature(sensor.temp, false)
          : formatAlertValue(alert, false),
        SensorTemperatureTooltip: (
          <ValueTooltip value={sensor ? formatTemperature(sensor.temp) : formatAlertValue(alert)} />
        )
      }}
    />
  )
}

function VoltageAlertTranslation({ alert, sensor, translationKey }: DeviceAlertTranslationProps) {
  return (
    <AlertTrans
      alert={alert}
      translationKey={translationKey}
      components={{
        value: sensor
          ? formatAlertValueBySensor(alert, sensor, false)
          : formatAlertValue(alert, false),
        ValueTooltip: (
          <ValueTooltip
            value={sensor ? formatAlertValueBySensor(alert, sensor) : formatAlertValue(alert)}
          />
        ),
        'sensor.v': sensor
          ? formatAlertValueBySensor(alert, sensor, false)
          : formatAlertValue(alert, false),
        SensorVoltageTooltip: (
          <ValueTooltip
            value={sensor ? formatAlertValueBySensor(alert, sensor) : formatAlertValue(alert)}
          />
        )
      }}
    />
  )
}

/**
 * --------------------------------------------------------------------------------------------------------------------
 * RuuviTag alerts
 * --------------------------------------------------------------------------------------------------------------------
 */

function BatteryAlertTranslation({
  alert,
  ruuviTagBattery,
  translationKey
}: RuuviTagAlertTranslationProps) {
  return (
    <AlertTrans
      alert={alert}
      translationKey={translationKey}
      components={{
        value:
          ruuviTagBattery?.voltage !== undefined
            ? formatBatteryVoltage(ruuviTagBattery.voltage, false, 0)
            : formatAlertValue(alert, false, 0),

        ValueTooltip: (
          <ValueTooltip
            value={
              ruuviTagBattery?.voltage !== undefined
                ? formatBatteryVoltage(ruuviTagBattery.voltage, true, 0)
                : formatAlertValue(alert, true, 0)
            }
          />
        )
      }}
    />
  )
}

function DewPointAlertTranslation({ alert, translationKey }: RuuviTagAlertTranslationProps) {
  return (
    <AlertTrans
      alert={alert}
      translationKey={translationKey}
      components={{
        ValueTooltip: <ValueTooltip value={formatAlertValue(alert)} />
      }}
    />
  )
}

function HumidityAlertTranslation({
  alert,
  sensor,
  ruuviTagCondition,
  translationKey
}: RuuviTagAlertTranslationProps & DeviceAlertTranslationProps) {
  if (alert.labels.ruuvitag) {
    return (
      <AlertTrans
        alert={alert}
        translationKey={translationKey}
        components={{
          value:
            ruuviTagCondition?.humidityAvg !== undefined
              ? formatHumidity(ruuviTagCondition.humidityAvg, false)
              : formatAlertValue(alert, false),
          ValueTooltip: (
            <ValueTooltip
              value={
                ruuviTagCondition?.humidityAvg !== undefined
                  ? formatHumidity(ruuviTagCondition.humidityAvg)
                  : formatAlertValue(alert)
              }
            />
          )
        }}
      />
    )
  }

  return (
    <DeviceHumidityAlertTranslation alert={alert} sensor={sensor} translationKey={translationKey} />
  )
}

function PressureAlertTranslation({
  alert,
  ruuviTagCondition,
  translationKey
}: RuuviTagAlertTranslationProps) {
  return (
    <AlertTrans
      alert={alert}
      translationKey={translationKey}
      components={{
        value:
          ruuviTagCondition?.pressureAvg !== undefined
            ? formatPressure(ruuviTagCondition.pressureAvg, false)
            : formatAlertValue(alert, false),
        ValueTooltip: (
          <ValueTooltip
            value={
              ruuviTagCondition?.pressureAvg !== undefined
                ? formatPressure(ruuviTagCondition.pressureAvg)
                : formatAlertValue(alert)
            }
          />
        )
      }}
    />
  )
}

function TemperatureAlertTranslation({
  alert,
  sensor,
  ruuviTagCondition,
  translationKey
}: RuuviTagAlertTranslationProps & DeviceAlertTranslationProps) {
  if (alert.labels.ruuvitag) {
    return (
      <AlertTrans
        alert={alert}
        translationKey={translationKey}
        components={{
          value:
            ruuviTagCondition?.temperatureAvg !== undefined
              ? formatTemperature(ruuviTagCondition.temperatureAvg, false)
              : formatAlertValue(alert, false),
          ValueTooltip: (
            <ValueTooltip
              value={
                ruuviTagCondition?.temperatureAvg !== undefined
                  ? formatTemperature(ruuviTagCondition.temperatureAvg)
                  : formatAlertValue(alert)
              }
            />
          )
        }}
      />
    )
  }

  return (
    <DeviceTemperatureAlertTranslation
      alert={alert}
      sensor={sensor}
      translationKey={translationKey}
    />
  )
}

/**
 * --------------------------------------------------------------------------------------------------------------------
 * Valve alerts.
 * --------------------------------------------------------------------------------------------------------------------
 */

function CondensationRiskAlertTranslation({
  alert,
  valveSensor,
  translationKey
}: ValveAlertTranslationProps) {
  switch (alert.labels.alertname) {
    case AlertRuleName.CONDENSATION_RISK_INTAKE:
      return (
        <AlertTrans
          alert={alert}
          translationKey={translationKey}
          components={{
            ValueTooltip: <AlertValueTooltip alert={alert} valveSensor={valveSensor} arrow />,
            ValveInTemperatureTooltip: (
              <ValveInTemperatureTooltip valveSensor={valveSensor} arrow />
            ),
            ValveInDewPointTooltip: <ValveInDewPointTooltip valveSensor={valveSensor} arrow />
          }}
        />
      )

    case AlertRuleName.CONDENSATION_RISK_EXHAUST:
      return (
        <AlertTrans
          alert={alert}
          translationKey={translationKey}
          components={{
            ValueTooltip: <AlertValueTooltip alert={alert} valveSensor={valveSensor} arrow />,
            ValveOutTemperatureTooltip: (
              <ValveOutTemperatureTooltip valveSensor={valveSensor} arrow />
            ),
            ValveOutDewPointTooltip: <ValveOutDewPointTooltip valveSensor={valveSensor} arrow />
          }}
        />
      )

    default:
      return null
  }
}

function ExhaustAirAlertTranslation({
  alert,
  valveSensor,
  translationKey
}: ValveAlertTranslationProps) {
  switch (alert.labels.alertname) {
    case AlertRuleName.EXHAUST_AIR_TOO_COLD:
      return (
        <AlertTrans
          alert={alert}
          translationKey={translationKey}
          components={{
            value: valveSensor
              ? formatAlertValueByValveSensor(alert, valveSensor, false)
              : formatAlertValue(alert, false),
            ValueTooltip: <AlertValueTooltip alert={alert} valveSensor={valveSensor} arrow />,
            ValveSetTemperatureTooltip: (
              <ValveSetTemperatureTooltip valveSensor={valveSensor} arrow />
            ),
            ValveInTemperatureTooltip: <ValveInTemperatureTooltip valveSensor={valveSensor} arrow />
          }}
        />
      )

    case AlertRuleName.EXHAUST_AIR_TOO_HOT:
      return (
        <AlertTrans
          alert={alert}
          translationKey={translationKey}
          components={{
            value: valveSensor
              ? formatAlertValueByValveSensor(alert, valveSensor, false)
              : formatAlertValue(alert, false),
            ValueTooltip: <AlertValueTooltip alert={alert} valveSensor={valveSensor} arrow />
          }}
        />
      )

    default:
      return null
  }
}

function HeatingWhileValveClosedAlertTranslation({
  alert,
  valveSensor,
  translationKey
}: ValveAlertTranslationProps) {
  return (
    <AlertTrans
      alert={alert}
      translationKey={translationKey}
      components={{
        'valve.inTemperature':
          valveSensor?.inTemperature !== undefined &&
          valveSensor.inTemperature !== null &&
          formatTemperature(valveSensor.inTemperature, false),
        'valve.outTemperature':
          valveSensor?.outTemperature !== undefined &&
          valveSensor?.outTemperature !== null &&
          formatTemperature(valveSensor.outTemperature, false),
        'valve.setPoint':
          valveSensor?.setPoint !== undefined && valveSensor?.setPoint !== null
            ? formatPercentage(valveSensor.setPoint / 100, false)
            : formatAlertParam2Value(alert, false),
        value: valveSensor
          ? formatAlertValueByValveSensor(alert, valveSensor, false)
          : formatAlertValue(alert, false),
        ValueTooltip: <AlertValueTooltip alert={alert} valveSensor={valveSensor} arrow />,
        ValveInTemperatureTooltip: <ValveInTemperatureTooltip valveSensor={valveSensor} arrow />,
        ValveOutTemperatureTooltip: <ValveOutTemperatureTooltip valveSensor={valveSensor} arrow />,
        ValveSetPointTooltip: <ValveSetPointTooltip valveSensor={valveSensor} arrow />
      }}
    />
  )
}

function NoHeatingAlertTranslation({
  alert,
  valveSensor,
  translationKey
}: ValveAlertTranslationProps) {
  return (
    <AlertTrans
      alert={alert}
      translationKey={translationKey}
      components={{
        'valve.inTemperature':
          valveSensor?.inTemperature !== undefined &&
          valveSensor?.inTemperature !== null &&
          formatTemperature(valveSensor.inTemperature, false),
        'valve.setPoint':
          valveSensor?.setPoint !== undefined && valveSensor?.setPoint !== null
            ? formatPercentage(valveSensor.setPoint / 100, false)
            : formatAlertParam2Value(alert, false),
        ValueTooltip: <AlertValueTooltip alert={alert} valveSensor={valveSensor} arrow />,
        ValveInTemperatureTooltip: <ValveInTemperatureTooltip valveSensor={valveSensor} arrow />,
        ValveSetPointTooltip: <ValveSetPointTooltip valveSensor={valveSensor} arrow />
      }}
    />
  )
}

function TargetTemperatureNotReachedAlertTranslation({
  alert,
  valveSensor,
  translationKey
}: ValveAlertTranslationProps) {
  return (
    <AlertTrans
      alert={alert}
      translationKey={translationKey}
      components={{
        'valve.inTemperature':
          valveSensor?.inTemperature !== undefined &&
          valveSensor?.inTemperature !== null &&
          formatTemperature(valveSensor.inTemperature, false),
        'valve.setTemperature':
          valveSensor?.setTemperature !== undefined &&
          valveSensor?.setTemperature !== null &&
          formatTemperature(valveSensor.setTemperature, false),
        'valve.setPoint':
          valveSensor?.setPoint !== undefined &&
          valveSensor?.setPoint !== null &&
          formatPercentage(valveSensor.setPoint / 100, false),
        value: valveSensor
          ? formatAlertValueByValveSensor(alert, valveSensor, false)
          : formatAlertValue(alert, false),
        ValueTooltip: <AlertValueTooltip alert={alert} valveSensor={valveSensor} arrow />,
        ValveInTemperatureTooltip: <ValveInTemperatureTooltip valveSensor={valveSensor} arrow />,
        ValveSetTemperatureTooltip: <ValveSetTemperatureTooltip valveSensor={valveSensor} arrow />,
        ValveSetPointTooltip: <ValveSetPointTooltip valveSensor={valveSensor} arrow />
      }}
    />
  )
}

function TemperatureRapidChangeAlertTranslation({
  alert,
  valveSensor,
  translationKey
}: ValveAlertTranslationProps) {
  switch (alert.labels.alertname) {
    case AlertRuleName.RAPID_TEMPERATURE_DROP:
    case AlertRuleName.RAPID_TEMPERATURE_RISE:
      return (
        <AlertTrans
          alert={alert}
          translationKey={translationKey}
          components={{
            'valve.inTemperature':
              valveSensor?.inTemperature !== undefined &&
              valveSensor?.inTemperature !== null &&
              formatTemperature(valveSensor.inTemperature, false),
            ValveInTemperatureTooltip: <ValveInTemperatureTooltip valveSensor={valveSensor} arrow />
          }}
        />
      )

    default:
      return null
  }
}

function WeakSignalAlertTranslation({
  alert,
  valveSensor,
  translationKey
}: ValveAlertTranslationProps) {
  return (
    <AlertTrans
      alert={alert}
      translationKey={translationKey}
      components={{
        value: valveSensor
          ? formatAlertValueByValveSensor(alert, valveSensor, false)
          : formatAlertValue(alert, false),
        ValueTooltip: <AlertValueTooltip alert={alert} valveSensor={valveSensor} arrow />,
        'valve.rssi': valveSensor?.rssi !== undefined && formatRssi(valveSensor.rssi, false),
        ValveRssiTooltip: <ValveRssiTooltip valveSensor={valveSensor} arrow />
      }}
    />
  )
}

interface ValueTooltipProps extends Omit<TooltipProps, 'title' | 'children'> {
  value?: string
  children?: ReactElement | ReactElement[]
}

function ValueTooltip({ value, children = <span />, ...props }: ValueTooltipProps) {
  return (
    <Tooltip title={value} {...props}>
      <span>{children}</span>
    </Tooltip>
  )
}

interface AlertValueTooltipProps extends Omit<ValueTooltipProps, 'value'> {
  alert: PrometheusAlert
  valveSensor?: ValveSensor | null
}

function AlertValueTooltip({ valveSensor, alert, ...props }: AlertValueTooltipProps) {
  return (
    <ValueTooltip
      value={
        valveSensor ? formatAlertValueByValveSensor(alert, valveSensor) : formatAlertValue(alert)
      }
      {...props}
    />
  )
}

interface ValveSensorTooltipProps extends Omit<TooltipProps, 'title' | 'children'> {
  valveSensor?: ValveSensor | null
  children?: ReactElement | ReactElement[]
}

function ValveInTemperatureTooltip({
  valveSensor,
  children = <span />,
  ...props
}: ValveSensorTooltipProps) {
  return (
    <Tooltip
      title={
        valveSensor?.inTemperature !== undefined && valveSensor.inTemperature !== null
          ? formatTemperature(valveSensor.inTemperature)
          : undefined
      }
      {...props}
    >
      <span>{children}</span>
    </Tooltip>
  )
}

function ValveInDewPointTooltip({
  valveSensor,
  children = <span />,
  ...props
}: ValveSensorTooltipProps) {
  return (
    <Tooltip
      title={
        valveSensor?.inDewPoint !== undefined && valveSensor.inDewPoint !== null
          ? formatTemperature(valveSensor.inDewPoint)
          : undefined
      }
      {...props}
    >
      <span>{children}</span>
    </Tooltip>
  )
}

function ValveOutTemperatureTooltip({
  valveSensor,
  children = <span />,
  ...props
}: ValveSensorTooltipProps) {
  return (
    <Tooltip
      title={
        valveSensor?.outTemperature !== undefined && valveSensor.outTemperature !== null
          ? formatTemperature(valveSensor.outTemperature)
          : undefined
      }
      {...props}
    >
      <span>{children}</span>
    </Tooltip>
  )
}

function ValveOutDewPointTooltip({
  valveSensor,
  children = <span />,
  ...props
}: ValveSensorTooltipProps) {
  return (
    <Tooltip
      title={
        valveSensor?.outDewPoint !== undefined && valveSensor.outDewPoint !== null
          ? formatTemperature(valveSensor.outDewPoint)
          : undefined
      }
      {...props}
    >
      <span>{children}</span>
    </Tooltip>
  )
}

function ValveSetTemperatureTooltip({
  valveSensor,
  children = <span />,
  ...props
}: ValveSensorTooltipProps) {
  return (
    <Tooltip
      title={
        valveSensor?.setTemperature !== undefined && valveSensor.setTemperature !== null
          ? formatTemperature(valveSensor.setTemperature)
          : undefined
      }
      {...props}
    >
      <span>{children}</span>
    </Tooltip>
  )
}

function ValveSetPointTooltip({
  valveSensor,
  children = <span />,
  ...props
}: ValveSensorTooltipProps) {
  return (
    <Tooltip
      title={
        valveSensor?.setPoint !== undefined && valveSensor.setPoint !== null
          ? formatValveSensorPosition(valveSensor.setPoint)
          : undefined
      }
      {...props}
    >
      <span>{children}</span>
    </Tooltip>
  )
}

function ValveRssiTooltip({ valveSensor, children = <span />, ...props }: ValveSensorTooltipProps) {
  return (
    <Tooltip
      title={
        valveSensor?.rssi !== undefined && valveSensor.rssi !== null
          ? formatRssi(valveSensor.rssi)
          : undefined
      }
      {...props}
    >
      <span>{children}</span>
    </Tooltip>
  )
}

interface AlertTransProps extends TransProps<string, 'alert'> {
  alert: PrometheusAlert
  translationKey?: AlertTranslationKey
}

function AlertTrans({ alert, translationKey = 'summary', components, ...props }: AlertTransProps) {
  /**
   * The theme.
   */
  const theme = useTheme()

  return (
    <Trans
      ns="alert"
      i18nKey={`AlertRule.${alert.labels.alertname}.${translationKey}`}
      components={{
        name: translateAlertPhase(alert),
        param1: formatAlertParam1Value(alert, false),
        param2: formatAlertParam2Value(alert, false),
        unit: getAlertValueUnit(alert),
        value: formatAlertValue(alert, false),
        ValueTooltip: <AlertValueTooltip alert={alert} arrow />,
        u: (
          <u
            style={{
              textDecoration: 'none',
              borderBottom: `1px solid ${theme.palette.text.secondary}`,
              color: theme.palette.text.secondary,
              cursor: 'pointer'
            }}
          />
        ),
        i: <i />,
        b: <b />,
        ...components
      }}
      {...props}
    />
  )
}
